import { eventChannel } from 'redux-saga';
import {
    call,
    put,
    take,
    cancelled,
    select,
    takeLatest,
    all,
    fork
} from 'redux-saga/effects';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    getDocs,
    startAfter
} from 'firebase/firestore';
import { db } from 'config/firebase';
import * as selectors from './Selectors';
import {
    clearNotificationsBadge,
    loadMoreNotifcationsFailure,
    loadMoreNotifcationsStart,
    loadMoreNotifcationsSuccess,
    markNotificationsReadSuccess,
    setNotifcationsError,
    setNotifications,
    updateUserNotificationsReadAt
} from 'store/actions/Notifications';
import UserNotificationsService from 'services/user-notifications-service';
import {
    LOAD_MORE_NOTIFICATIONS,
    MARK_NOTIFICATIONS_READ_REQUEST
} from 'store/actions/types';

const LIVE_LIMIT = 100;
const PAGE = 30;

const toMillis = value => {
    if (!value) return null;

    if (typeof value.toMillis === 'function') {
        return value.toMillis();
    }

    const seconds = value.seconds ?? value._seconds;
    const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;

    if (typeof seconds === 'number') {
        return seconds * 1000 + Math.floor(nanoseconds / 1000000);
    }

    return null;
};

const createNotificationsChannel = uid =>
    eventChannel(emit => {
        const liveQuery = query(
            collection(db, 'notifications'),
            where('members', 'array-contains', uid),
            orderBy('created_at', 'desc'),
            limit(LIVE_LIMIT)
        );

        const unsubscribe = onSnapshot(
            liveQuery,
            snapshot => {
                const items = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                emit(items);
            },
            error => {
                emit({ error });
            }
        );

        return () => unsubscribe();
    });

export async function loadMoreNotifications(uid, cursor) {
    const notificationsQuery = query(
        collection(db, 'notifications'),
        where('members', 'array-contains', uid),
        orderBy('created_at', 'desc'),
        startAfter(cursor),
        limit(PAGE)
    );

    const snapshot = await getDocs(notificationsQuery);

    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));
}

export function* notificationsListener() {
    const initialUserData = yield select(selectors._userData);
    const uid = initialUserData?.uid;

    if (!uid) return;

    const channel = yield call(createNotificationsChannel, uid);

    try {
        while (true) {
            const channelData = yield take(channel);

            if (channelData?.error) {
                yield put(setNotifcationsError(channelData.error.message));
                continue;
            }

            const userData = yield select(selectors._userData);

            const lastReadAtMs = toMillis(userData?.notifications_read_at);

            const items = channelData.map(notification => {
                const notificationCreatedAtMs = toMillis(notification.created_at);

                return {
                    ...notification,
                    unread: lastReadAtMs ? notificationCreatedAtMs > lastReadAtMs : true
                };
            });

            const unreadCount = items.filter(item => item.unread).length;

            yield put(
                setNotifications({
                    items,
                    badge: unreadCount >= 99 ? '99+' : String(unreadCount),
                    unreadCount,
                    hasMore: channelData.length === LIVE_LIMIT
                })
            );
        }
    } finally {
        if (yield cancelled()) {
            channel.close();
        }
    }
}

function* loadMoreNotificationsSaga() {
    try {
        const userData = yield select(selectors._userData);
        const uid = userData?.uid;

        if (!uid) return;

        const { items, isLoadingMore, hasMore } = yield select(
            state => state.notifications
        );

        if (isLoadingMore || !hasMore) return;

        const cursor = items[items.length - 1]?.created_at;

        if (!cursor) return;

        yield put(loadMoreNotifcationsStart());

        const moreItems = yield call(loadMoreNotifications, uid, cursor);
        const lastReadAtMs = toMillis(userData?.notifications_read_at);

        const mappedItems = moreItems.map(notification => {
            const notificationCreatedAtMs = toMillis(notification.created_at);

            return {
                ...notification,
                unread: lastReadAtMs ? notificationCreatedAtMs > lastReadAtMs : true
            };
        });
        yield put(
            loadMoreNotifcationsSuccess({
                items: mappedItems,
                hasMore: moreItems.length === PAGE
            })
        );
    } catch (error) {
        yield put(loadMoreNotifcationsFailure(error.message));
    }
}

export function* notificationsPaginationWatcher() {
    yield takeLatest(LOAD_MORE_NOTIFICATIONS, loadMoreNotificationsSaga);
}

function* markNotificationsReadSaga() {
    try {
        const response = yield call([
            UserNotificationsService,
            UserNotificationsService.markAllRead
        ]);

        yield put(clearNotificationsBadge());

        yield put(updateUserNotificationsReadAt(response.notifications_read_at));
        yield put(markNotificationsReadSuccess());
    } catch (error) {
        console.error('markNotificationsReadSaga error:', error);
        yield put(setNotifcationsError(error.message));
    }
}
export function* markNotificationsReadWatcher() {
    yield takeLatest(MARK_NOTIFICATIONS_READ_REQUEST, markNotificationsReadSaga);
}

export function* notificationsSaga() {
    yield all([
        fork(notificationsListener),
        fork(notificationsPaginationWatcher),
        fork(markNotificationsReadWatcher)
    ]);
}
